import { ArrowLeft, Eye, EyeOff, Lock } from "lucide-react";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { verifyForgotPasswordApi } from "../../api/authAPI";
import logger from "../../utils/logger";

// ✅ Validation Schema with Yup
const schema = yup.object().shape({
  password: yup
    .string()
    .required("Password is required")
    .matches(/^\S+$/, "Spaces are not allowed in password")
    .min(8, "At least 8 characters")
    .matches(/[A-Z]/, "Must contain an uppercase letter")
    .matches(/[a-z]/, "Must contain a lowercase letter")
    .matches(/\d/, "Must contain a number")
    .matches(/[@$!%*?&#]/, "Must contain a special character"),
  confirmPassword: yup
    .string()
    .matches(/^\S+$/, "Spaces are not allowed in password")
    .oneOf([yup.ref("password")], "Passwords must match")
    .required("Confirm password is required"),
});

const SetPassword = () => {
  const location = useLocation();
  const phone = location?.state?.phone;
  const navigate = useNavigate();

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // ✅ Setup React Hook Form
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
  });

  const onSubmit = async (data: any) => {
    try {
      let payload = {
        phone,
        password: data.password,
        confirmPassword: data.confirmPassword,
      };

      let res = await verifyForgotPasswordApi(payload);
      let response = res?.data;

      if (response?.status) {
        toast.success(response?.message || "Password updated successfully");
        reset();
        navigate("/");
      } else {
        toast.error(response?.message || "Failed to update password");
      }
    } catch (error: any) {
      logger.error("set-password-error", error);
      toast.error(error?.message || "Failed to update password");
    }
  };

  return (
    <div className="w-full max-w-md mx-auto">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="bg-white/10 backdrop-blur-sm rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
          <Lock className="w-8 h-8 text-white" />
        </div>
        <h1 className="text-3xl font-bold text-white mb-2">Set New Password</h1>
        <p className="text-blue-100">
          Create a secure password for your account
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Password */}
        <div>
          <p className="block text-sm font-medium text-blue-100 mb-2">
            New Password
          </p>

          <div className="relative">
            <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-blue-300" />
            <input
              type={showPassword ? "text" : "password"}
              {...register("password")}
              onKeyDown={(e) => {
                if (e.key === " ") {
                  e.preventDefault();
                }
              }}
              onCopy={(e) => e.preventDefault()}
              onCut={(e) => e.preventDefault()}
              onPaste={(e) => e.preventDefault()}
              className="w-full pl-10 pr-12 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all"
              placeholder="Enter new password"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-blue-300 hover:text-white transition-colors"
            >
              {showPassword ? (
                <Eye className="w-5 h-5" />
              ) : (
                <EyeOff className="w-5 h-5" />
              )}
            </button>
          </div>
          {errors.password && (
            <p className="text-red-400 text-sm mt-1">
              {errors.password.message as string}
            </p>
          )}
        </div>

        {/* Confirm Password */}
        <div>
          <p className="block text-sm font-medium text-blue-100 mb-2">
            Confirm New Password
          </p>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-blue-300" />
            <input
              type={showConfirmPassword ? "text" : "password"}
              {...register("confirmPassword")}
              onKeyDown={(e) => {
                if (e.key === " ") {
                  e.preventDefault();
                }
              }}
              onCopy={(e) => e.preventDefault()}
              onCut={(e) => e.preventDefault()}
              onPaste={(e) => e.preventDefault()}
              className="w-full pl-10 pr-12 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all"
              placeholder="Confirm new password"
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-blue-300 hover:text-white transition-colors"
            >
              {showConfirmPassword ? (
                <Eye className="w-5 h-5" />
              ) : (
                <EyeOff className="w-5 h-5" />
              )}
            </button>
          </div>
          {errors.confirmPassword && (
            <p className="text-red-400 text-sm mt-1">
              {errors.confirmPassword.message as string}
            </p>
          )}
        </div>

        {/* Requirements */}
        <div className="bg-white/5 backdrop-blur-sm rounded-lg p-4 border border-white/10">
          <h3 className="text-white font-medium mb-2">
            Password Requirements:
          </h3>
          <ul className="text-sm text-blue-100 space-y-1">
            <li className="flex items-center gap-2">
              <span className="w-2 h-2 bg-green-400 rounded-full"></span>
              <span>At least 8 characters long</span>
            </li>

            <li className="flex items-center gap-2">
              <span className="w-2 h-2 bg-green-400 rounded-full"></span>
              <span>Contains uppercase and lowercase letters</span>
            </li>

            <li className="flex items-center gap-2">
              <span className="w-2 h-2 bg-green-400 rounded-full"></span>
              <span>Contains at least one number</span>
            </li>

            <li className="flex items-center gap-2">
              <span className="w-2 h-2 bg-green-400 rounded-full"></span>
              <span>Contain at least one special character</span>
            </li>
          </ul>
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-white text-slate-800 font-semibold py-3 px-6 rounded-lg hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-blue-600 transition-all transform hover:scale-[1.02] active:scale-[0.98]"
        >
          {isSubmitting ? "Updating..." : "Update Password"}
        </button>

        {/* Back */}
        <div className="text-center">
          <button
            type="button"
            onClick={() => navigate("/", { replace: true })}
            className="inline-flex items-center text-blue-100 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Login
          </button>
        </div>
      </form>
    </div>
  );
};

export default SetPassword;
